import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Stack,
  useToast,
  Badge,
  Switch,
  IconButton,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Tab,
  Icon,
  InputGroup,
  InputLeftAddon,
  Select as ChakraSelect,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useFieldArray,
  useForm,
  FormProvider,
  useWatch,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import type { CreateCampaignFlowDraft } from "./components/CreateCampaignModal";
import { type BudgetData } from "./components/BudgetSelector";
import Container from "./components/Container";
import WhiteBgInput from "./WhiteBgInput";
import { FacebookPage, InstagramAccount } from "./types";
import actions from "../../../../../actions";
import InstagramPostPreview from "./components/AdPreview/InstagramPostPreview";
import InstagramStoryPreview from "./components/AdPreview/InstagramStoryPreview";
import FacebookFeedPreview from "./components/AdPreview/FacebookFeedPreview";
import FacebookStoryPreview from "./components/AdPreview/FacebookStoryPreview";
import { FaChevronLeft } from "react-icons/fa";
import CreateCampaignReview from "./CreateCampaignReview";
import type { ReviewEditorSection } from "./CreateCampaignReview";
import CreativeBlockEditor from "./components/CreativeBlockEditor";
import AudienceCard from "./components/AudienceCard";
import type { AudienceData } from "@common/interfaces/KYDMarketing";
import { mapCustomAudiencesToServerFormat } from "./transform/customAudienceMapper";
import dayjs from "dayjs";
import {
  CreateCampaignFlowSchema,
  CreateCampaignFlowValues,
  PRIMARY_TEXT_MAX,
} from "./forms/campaignFlowForm";
import KYDLoading from "src/ui/KYDLoading";
import { loadingMessageToText } from "../util";

export type CreateCampaignFlowProps = {
  draft: CreateCampaignFlowDraft;
  adAccounts: { id: string; name: string }[];
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  pixels: { id: string; name: string }[];
  isRelatedDataLoading: boolean;
  createUploadUrl: (
    file: File,
    callback: (params: {
      signedUrl?: string;
      fileKey?: string;
      file_key?: string;
      cdn_url?: string;
    }) => void
  ) => void;
  onBack: () => void;
  onLaunchSuccess: () => void;
  publishStatusText?: string | null;
  toast: ReturnType<typeof useToast>;
  onRefetchInstagramForPage?: (facebookPageId: string) => void;
  handleCreateCampaign: ({
    ad_account_id,
    event_id,
    creatives,
    budget,
    schedule,
    targeting,
  }: {
    ad_account_id: string;
    event_id: string;
    creatives: CreativeBlock[];
    budget: BudgetData;
    schedule: {
      start_at: string;
      end_at: string;
    };
    targeting: {
      strategy: "custom" | "template";
      custom_audiences?: AudienceData[];
    };
  }) => void;
};

interface CampaignDefaults {
  event_id: string;
  ad_account_id: string;
  pixel_id: string;
  campaign_name: string;
  primary_text_templates: string[];
  creative_images: { src: string; type: "square" | "portrait" }[];
  budget: {
    type: "lifetime" | "daily";
    amount: number;
    lifetime: number;
  };
  schedule: {
    start_at: string;
    end_at: string;
  };
  targeting: {
    strategy: "template" | "custom";
    resolved_strategies: { id: string; name: string; description: string }[];
    static_interests?: { id: string; name?: string }[];
  };
  instagram_id?: string;
  fb_page_id?: string;
}

export type CreativeBlock = {
  /** Custom display name; when unset, shown as "Ad {index + 1}" */
  name?: string;
  squareMediaUrl?: string;
  squareMediaFileKey?: string;
  squareMediaType?: "image" | "video";
  verticalMediaUrl?: string;
  verticalMediaFileKey?: string;
  verticalMediaType?: "image" | "video";
  primaryText: string;
  fbPage: FacebookPage | null;
  instagramAccount: InstagramAccount | null;
};

const selectContainerStyles = {
  container: () => ({
    position: "relative" as const,
    width: "100%",
    backgroundColor: "white",
  }),
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 140,
  }),
  placeholder: (provided: Record<string, unknown>) => ({
    ...provided,
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};

function SectionHeader({
  title,
  subtitle,
  isComplete,
  isCollapsed,
  onToggle,
}: {
  title: React.ReactNode;
  subtitle?: string;
  isComplete?: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      w="full"
      py={2}
      onClick={onToggle}
      _hover={{ bg: "gray.50" }}
      rounded="md"
    >
      <HStack justify="space-between" align="center" w="full">
        <HStack align="center" spacing={2} flex={1}>
          {isComplete ? (
            <Icon
              as={CheckCircleIcon}
              color="green.500"
              boxSize={"20px"}
              flexShrink={0}
            />
          ) : (
            <Icon
              as={CheckCircleIcon}
              color="gray.400"
              boxSize={"20px"}
              flexShrink={0}
            />
          )}
          <Box>
            <Text fontSize="20px" fontWeight="semibold" lineHeight={"20px"}>
              {title}
            </Text>
            {subtitle && (
              <Text fontSize="sm" color="gray.600" fontWeight="normal">
                {subtitle}
              </Text>
            )}
          </Box>
        </HStack>
        <IconButton
          aria-label={isCollapsed ? "Expand" : "Collapse"}
          variant="ghost"
          size="sm"
          icon={
            isCollapsed ? (
              <ChevronDownIcon fontSize="lg" />
            ) : (
              <ChevronUpIcon fontSize="lg" />
            )
          }
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        />
      </HStack>
    </Box>
  );
}

export default function CreateCampaignFlow({
  draft,
  adAccounts,
  pages,
  instagramAccounts,
  isRelatedDataLoading,
  createUploadUrl,
  onBack,
  onLaunchSuccess,
  publishStatusText,
  toast,
  onRefetchInstagramForPage,
  handleCreateCampaign,
}: CreateCampaignFlowProps) {
  /** When set, the user is renaming this creative; value is the current input. */
  const [editingCreativeName, setEditingCreativeName] = useState<{
    index: number;
    value: string;
  } | null>(null);
  const [defaults, setDefaults] = useState<CampaignDefaults | null>(null);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [
    hasCompletedInitialDefaultsFetch,
    setHasCompletedInitialDefaultsFetch,
  ] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [budgetUpdatedByUser, setBudgetUpdatedByUser] = useState(false);
  const [adCreativeOpen, setAdCreativeOpen] = useState(true);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<"editor" | "preview">(
    "editor"
  );
  const [step, setStep] = useState<"edit" | "review">("edit");
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);

  const uploadRefs = useRef<
    Record<string, { fileKey?: string; cdn_url?: string }>
  >({});
  const applyingDefaultsBudgetRef = useRef(false);
  const hasHydratedDefaultsRef = useRef(false);

  const form = useForm<CreateCampaignFlowValues>({
    resolver: yupResolver(CreateCampaignFlowSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      creatives: [
        {
          primaryText: "",
          fbPage: null,
          instagramAccount: null,
        },
      ],
      smart_targeting: true,
      custom_audiences: [],
      audience_search_query: "",
      budget: {
        type: "lifetime",
        amount: {
          amount: null,
          currency: "USD",
        },
      },
      schedule: {
        start_at: "",
        end_at: "",
      },
    },
  });

  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors, isValid },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "creatives",
  });

  const creatives = useWatch({ control, name: "creatives" }) ?? [];
  const customAudiences = useWatch({ control, name: "custom_audiences" }) ?? [];
  const smartTargeting = useWatch({ control, name: "smart_targeting" });
  const budget = useWatch({ control, name: "budget" });
  const schedule = useWatch({ control, name: "schedule" });

  const scheduleStart = schedule?.start_at ?? "";
  const scheduleEnd = schedule?.end_at ?? "";
  const reviewHistoryStateKey = "__kyd_create_campaign_review";

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    if (step !== "review") return;

    if (typeof window !== "undefined") {
      const currentState = window.history.state ?? {};
      if (!currentState?.[reviewHistoryStateKey]) {
        window.history.pushState(
          { ...currentState, [reviewHistoryStateKey]: true },
          ""
        );
      }
    }

    const handlePopState = () => {
      setStep("edit");
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [reviewHistoryStateKey, step]);

  const budgetCents =
    budget?.amount?.amount != null
      ? Math.round(budget.amount.amount * 100)
      : undefined;
  const [debouncedBudgetCents, setDebouncedBudgetCents] = useState<
    number | undefined
  >(budgetCents);
  const [debouncedBudgetType, setDebouncedBudgetType] = useState<
    "daily" | "lifetime"
  >(budget?.type === "daily" ? "daily" : "lifetime");

  useEffect(() => {
    const nextBudgetType = budget?.type === "daily" ? "daily" : "lifetime";

    if (!budgetUpdatedByUser) {
      setDebouncedBudgetCents(budgetCents);
      setDebouncedBudgetType(nextBudgetType);
      return;
    }

    const timeout = setTimeout(() => {
      setDebouncedBudgetCents(budgetCents);
      setDebouncedBudgetType(nextBudgetType);
    }, 500); // 500ms debounce time for budget changes

    return () => clearTimeout(timeout);
  }, [budgetUpdatedByUser, budgetCents, budget?.type]);

  const createUploadUrlForKYD = useCallback(
    (slotKey: string) =>
      ({
        file,
        callback,
      }: {
        file: File;
        callback: (r: { signedUrl: string }) => void | Promise<string | void>;
      }) => {
        return new Promise<{ signedUrl: string; fileKey: string }>(
          (resolve) => {
            createUploadUrl(file, (res: any) => {
              const fileKey = res?.fileKey ?? res?.file_key ?? "";
              const signedUrl = res?.signedUrl ?? res?.signed_url ?? "";
              const cdn_url = res?.cdn_url ?? "";
              uploadRefs.current[slotKey] = { fileKey, cdn_url };
              callback({ signedUrl });
              resolve({ signedUrl, fileKey });
            });
          }
        );
      },
    [createUploadUrl]
  );

  useEffect(() => {
    if (pages.length === 0 && instagramAccounts.length === 0) return;

    const firstCreative = getValues("creatives.0");
    if (!firstCreative) return;

    const patch: Partial<CreativeBlock> = {};
    if (!firstCreative.fbPage && pages[0]) {
      patch.fbPage = pages[0];
    }
    if (!firstCreative.instagramAccount && instagramAccounts[0]) {
      patch.instagramAccount = instagramAccounts[0];
    }

    if (Object.keys(patch).length === 0) return;

    setValue(
      "creatives.0",
      { ...firstCreative, ...patch },
      { shouldValidate: true }
    );
  }, [pages, instagramAccounts, getValues, setValue]);

  useEffect(() => {
    setBudgetUpdatedByUser(false);
    applyingDefaultsBudgetRef.current = false;
    hasHydratedDefaultsRef.current = false;
    setHasCompletedInitialDefaultsFetch(false);
  }, [draft.eventId, draft.adAccountId]);

  useEffect(() => {
    if (!draft.eventId || !draft.adAccountId) return;
    const currentBudgetType = budget?.type === "daily" ? "daily" : "lifetime";
    const isBudgetDebounced =
      debouncedBudgetCents === budgetCents &&
      debouncedBudgetType === currentBudgetType;
    if (budgetUpdatedByUser && !isBudgetDebounced) return;
    if (applyingDefaultsBudgetRef.current) {
      applyingDefaultsBudgetRef.current = false;
      return;
    }

    const shouldUseBudget = budgetUpdatedByUser && debouncedBudgetCents != null;
    const isInitialDefaultsFetch = !hasCompletedInitialDefaultsFetch;
    setLoadingDefaults(true);
    actions.ADMIN.fetchCampaignDefaults({
      event_id: draft.eventId,
      ad_account_id: draft.adAccountId,
      ...(shouldUseBudget
        ? {
            budget: debouncedBudgetCents,
            budget_type: debouncedBudgetType,
          }
        : {}),
    })
      .then((res: any) => {
        setDefaults((prev) => {
          const isFirstHydration = !hasHydratedDefaultsRef.current || !prev;
          if (isFirstHydration) {
            hasHydratedDefaultsRef.current = true;
            return res;
          }

          if (!getValues("smart_targeting") || !res?.targeting) {
            return prev;
          }

          return {
            ...prev,
            targeting: res.targeting,
          };
        });
      })
      .catch(() => setDefaults(null))
      .finally(() => {
        setLoadingDefaults(false);
        if (isInitialDefaultsFetch) {
          setHasCompletedInitialDefaultsFetch(true);
        }
      });
  }, [
    draft.eventId,
    draft.adAccountId,
    debouncedBudgetCents,
    debouncedBudgetType,
    budgetUpdatedByUser,
    budgetCents,
    budget?.type,
    hasCompletedInitialDefaultsFetch,
  ]);

  useEffect(() => {
    if (!defaults?.budget) return;
    const nextType = defaults.budget.type;
    const nextAmount = defaults.budget.amount / 100;
    const currentBudget = getValues("budget");

    if (
      currentBudget?.type === nextType &&
      currentBudget?.amount?.amount === nextAmount
    ) {
      return;
    }

    applyingDefaultsBudgetRef.current = true;
    setValue(
      "budget",
      {
        type: nextType,
        amount: {
          amount: nextAmount,
          currency: currentBudget?.amount?.currency ?? "USD",
        },
      },
      { shouldValidate: true }
    );
  }, [defaults?.budget, getValues, setValue]);

  useEffect(() => {
    const templates = defaults?.primary_text_templates;
    if (!templates?.length) return;

    const firstTemplate = templates[0];
    if (!firstTemplate || typeof firstTemplate !== "string") return;

    const firstCreative = getValues("creatives.0");
    if (!firstCreative) return;
    if (firstCreative.primaryText.trim().length > 0) return;

    setValue(
      "creatives.0",
      {
        ...firstCreative,
        primaryText: firstTemplate.slice(0, PRIMARY_TEXT_MAX),
      },
      { shouldValidate: true }
    );
  }, [defaults?.primary_text_templates, getValues, setValue]);

  useEffect(() => {
    const images = defaults?.creative_images;
    if (!images?.length) return;

    const squareImage = images.find(
      (image) => image.type === "square" && image.src
    )?.src;
    const portraitImage = images.find(
      (image) => image.type === "portrait" && image.src
    )?.src;

    if (!squareImage && !portraitImage) return;

    const firstCreative = getValues("creatives.0");
    if (!firstCreative) return;

    const shouldPrefillSquare = !firstCreative.squareMediaUrl && !!squareImage;
    const shouldPrefillPortrait =
      !firstCreative.verticalMediaUrl && !!portraitImage;

    if (!shouldPrefillSquare && !shouldPrefillPortrait) return;

    setValue(
      "creatives.0",
      {
        ...firstCreative,
        ...(shouldPrefillSquare
          ? { squareMediaUrl: squareImage, squareMediaType: "image" as const }
          : {}),
        ...(shouldPrefillPortrait
          ? {
              verticalMediaUrl: portraitImage,
              verticalMediaType: "image" as const,
            }
          : {}),
      },
      { shouldValidate: true }
    );
  }, [defaults?.creative_images, getValues, setValue]);

  useEffect(() => {
    const defaultSchedule = defaults?.schedule;
    if (!defaultSchedule?.start_at || !defaultSchedule?.end_at) return;

    const currentSchedule = getValues("schedule");
    const toDateTimeLocal = (iso: string) =>
      dayjs(iso).format("YYYY-MM-DDTHH:mm");

    if (!currentSchedule.start_at) {
      setValue("schedule.start_at", toDateTimeLocal(defaultSchedule.start_at), {
        shouldValidate: true,
      });
    }

    if (!currentSchedule.end_at) {
      setValue("schedule.end_at", toDateTimeLocal(defaultSchedule.end_at), {
        shouldValidate: true,
      });
    }
  }, [defaults?.schedule, getValues, setValue]);

  const pageOptions = pages.map((p) => ({ label: p.name, value: p }));
  const instagramOptions = instagramAccounts.map((i) => ({
    label: i.username,
    value: i,
  }));

  const addCreative = useCallback(() => {
    const templates = defaults?.primary_text_templates ?? [];
    const nextTemplate =
      templates.length > 0
        ? templates[creatives.length % templates.length]
        : "";

    append({
      primaryText: nextTemplate.slice(0, PRIMARY_TEXT_MAX),
      fbPage: creatives[0]?.fbPage ?? pages[0] ?? null,
      instagramAccount:
        creatives[0]?.instagramAccount ?? instagramAccounts[0] ?? null,
    });
    setEditingCreativeName(null);
  }, [
    append,
    creatives.length,
    defaults?.primary_text_templates,
    pages,
    instagramAccounts,
  ]);

  const updateCreative = useCallback(
    (index: number, patch: Partial<CreativeBlock>) => {
      const creative = getValues(`creatives.${index}`);
      if (!creative) return;

      setValue(
        `creatives.${index}`,
        { ...creative, ...patch },
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [getValues, setValue]
  );

  const removeCreative = useCallback(
    (index: number) => {
      if (creatives.length <= 1) return;
      if (editingCreativeName?.index === index) {
        setEditingCreativeName(null);
      } else if (
        editingCreativeName != null &&
        editingCreativeName.index > index
      ) {
        setEditingCreativeName({
          ...editingCreativeName,
          index: editingCreativeName.index - 1,
        });
      }
      remove(index);
    },
    [creatives.length, editingCreativeName, remove]
  );

  const ctaText = "Shop Now";
  const eventName = draft.event?.name ?? "Event";
  const adAccountName =
    adAccounts.find((a) => a.id === draft.adAccountId)?.name ?? "—";
  const eventDescription =
    (draft.event as { description?: string } | undefined)?.description ?? "";
  const websiteUrl = draft.event?.link ?? "";

  const isCreativeSectionComplete = !errors.creatives;
  const hasRequiredAudience =
    smartTargeting || (customAudiences?.length ?? 0) > 0;
  const isAudienceSectionComplete =
    hasRequiredAudience && (smartTargeting || !errors.custom_audiences);
  const isBudgetSectionComplete =
    !errors.budget &&
    !errors.schedule &&
    Boolean(scheduleStart) &&
    Boolean(scheduleEnd);

  const targetingForReview =
    smartTargeting && defaults?.targeting
      ? {
          strategy: "template" as const,
          resolved_strategies: defaults?.targeting?.resolved_strategies ?? [],
        }
      : {
          strategy: "custom" as const,
          resolved_strategies: customAudiences ?? [],
        };

  const buildCreateCampaignPayload = useCallback(
    (values: CreateCampaignFlowValues) => {
      const customAudiencesPayload =
        !values.smart_targeting &&
        mapCustomAudiencesToServerFormat(values.custom_audiences ?? []);

      return {
        ad_account_id: draft.adAccountId,
        event_id: draft.eventId,
        creatives: (values.creatives ?? []) as CreativeBlock[],
        budget: {
          type: values.budget?.type === "daily" ? "daily" : "lifetime",
          amount: {
            amount: values.budget?.amount?.amount ?? 0,
            currency: values.budget?.amount?.currency ?? "USD",
          },
        },
        schedule: {
          start_at: values.schedule?.start_at ?? "",
          end_at: values.schedule?.end_at ?? "",
        },
        targeting: {
          strategy: values.smart_targeting
            ? ("template" as const)
            : ("custom" as const),
          ...(customAudiencesPayload?.length
            ? { custom_audiences: customAudiencesPayload }
            : {}),
        },
      };
    },
    [draft.adAccountId, draft.eventId]
  );

  const handleLaunchCampaign = useCallback(async () => {
    const valid = await trigger();
    if (!valid) {
      setStep("edit");
      return;
    }

    setLaunching(true);
    try {
      const payload = buildCreateCampaignPayload(getValues());
      await handleCreateCampaign(payload);
      onLaunchSuccess();
    } catch (err: any) {
      toast({
        title: "Failed to launch campaign",
        description: err?.message ?? String(err),
        status: "error",
      });
    } finally {
      setLaunching(false);
    }
  }, [
    buildCreateCampaignPayload,
    getValues,
    handleCreateCampaign,
    onLaunchSuccess,
    toast,
    trigger,
  ]);

  const handleReviewCampaign = useCallback(async () => {
    const valid = await trigger();
    if (!valid || !hasRequiredAudience) return;
    setStep("review");
  }, [hasRequiredAudience, trigger]);

  const handleBackToEditor = useCallback(
    (section: ReviewEditorSection = "general") => {
      setStep("edit");
      setMobilePanel("editor");

      if (section === "budget") {
        setBudgetOpen(true);
        setAudienceOpen(false);
      }

      if (section === "audience") {
        setAudienceOpen(true);
        setBudgetOpen(false);
      }

      if (section === "creative") {
        setAdCreativeOpen(true);
      }
    },
    []
  );

  const isBlockingDataLoading =
    isRelatedDataLoading ||
    (loadingDefaults && !hasCompletedInitialDefaultsFetch);
  const loadingModal = (
    <Modal isOpen={isBlockingDataLoading} onClose={() => {}} isCentered>
      <ModalOverlay />
      <ModalContent
        w="220px"
        py={8}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.800"
        borderRadius="xl"
      >
        <VStack spacing={3}>
          <KYDLoading withText="Loading campaign data..." />
        </VStack>
      </ModalContent>
    </Modal>
  );

  const normalizedPublishStatusText =
    loadingMessageToText(publishStatusText ?? "") ?? publishStatusText;
  const publishStatusToast =
    launching && normalizedPublishStatusText ? (
      <Box
        position="fixed"
        bottom={6}
        right={6}
        zIndex={1401}
        bg="rgba(74, 85, 104, 0.85)"
        color="white"
        px={4}
        py={3}
        borderRadius="md"
        shadow="lg"
      >
        <Text fontSize="sm" fontWeight="medium">
          {normalizedPublishStatusText}
        </Text>
      </Box>
    ) : null;

  const leaveEditorModal = (
    <Modal
      isOpen={isExitConfirmOpen}
      onClose={() => setIsExitConfirmOpen(false)}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Leave editor?</ModalHeader>
        <ModalBody>
          <Text>
            All changes made to this campaign will be lost, are you sure you
            want to leave the editor?
          </Text>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              bg="black"
              color="white"
              _hover={{ bg: "gray.800" }}
              onClick={() => setIsExitConfirmOpen(false)}
            >
              No
            </Button>
            <Button
              bg="white"
              color="black"
              borderWidth="1px"
              borderColor="gray.300"
              _hover={{ bg: "gray.100" }}
              onClick={() => {
                setIsExitConfirmOpen(false);
                onBack();
              }}
            >
              Yes
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  if (step === "review") {
    return (
      <>
        <CreateCampaignReview
          event={
            draft.event
              ? {
                  name: draft.event.name,
                  display_start_at: draft.event.display_start_at,
                  venue: draft.event.venue
                    ? {
                        name: draft.event.venue.name,
                        address: draft.event.venue.address,
                      }
                    : undefined,
                  image: draft.event.image,
                }
              : null
          }
          adAccountName={adAccountName}
          budget={budget as BudgetData}
          scheduleStart={scheduleStart}
          scheduleEnd={scheduleEnd}
          locationLabel={
            [draft.event?.venue?.name, draft.event?.venue?.address]
              .filter(Boolean)
              .join(", ") || undefined
          }
          targeting={targetingForReview}
          creatives={(creatives ?? []) as CreativeBlock[]}
          onBackToEditor={handleBackToEditor}
          onLaunch={handleLaunchCampaign}
          launching={launching}
        />
        {loadingModal}
        {publishStatusToast}
      </>
    );
  }

  return (
    <>
      <FormProvider {...form}>
        <VStack align="stretch" spacing={4} w="full">
          {/* Mobile-only: Editor / Preview toggle */}
          <HStack
            display={{ base: "flex", md: "none" }}
            justify="center"
            w="full"
            py={2}
          >
            <HStack
              bg="gray.200"
              rounded="lg"
              p={1}
              spacing={0}
              role="tablist"
              aria-label="Editor or Preview"
            >
              <Button
                size="sm"
                variant="ghost"
                flex={1}
                minW="100px"
                rounded="md"
                bg={mobilePanel === "editor" ? "white" : "transparent"}
                shadow={mobilePanel === "editor" ? "sm" : "none"}
                onClick={() => setMobilePanel("editor")}
                fontWeight="medium"
                fontSize="sm"
              >
                Editor
              </Button>
              <Button
                size="sm"
                variant="ghost"
                flex={1}
                minW="100px"
                rounded="md"
                bg={mobilePanel === "preview" ? "white" : "transparent"}
                shadow={mobilePanel === "preview" ? "sm" : "none"}
                onClick={() => setMobilePanel("preview")}
                fontWeight="medium"
                fontSize="sm"
              >
                Preview
              </Button>
            </HStack>
          </HStack>

          <HStack align="stretch" spacing={6} w="full" flex={1} minH={0}>
            {/* Left panel – form card */}
            <Container
              flex={1}
              overflow="auto"
              bgColor="#fafafa"
              display={{
                base: mobilePanel === "editor" ? "block" : "none",
                md: "block",
              }}
            >
              <VStack align="stretch" spacing={0}>
                {/* Ad Creative */}
                <Box pb={8} borderBottomWidth="1px" borderColor="gray.100">
                  <SectionHeader
                    title="Ad Creative"
                    subtitle="Upload your ad media and text."
                    isComplete={isCreativeSectionComplete}
                    isCollapsed={!adCreativeOpen}
                    onToggle={() => setAdCreativeOpen((o) => !o)}
                  />
                  {adCreativeOpen && (
                    <VStack align="stretch" spacing={5} mt={4}>
                      {fields.map((field, index) => (
                        <CreativeBlockEditor
                          key={field.id}
                          creative={
                            (creatives[index] ?? {
                              primaryText: "",
                              fbPage: null,
                              instagramAccount: null,
                            }) as CreativeBlock
                          }
                          index={index}
                          totalCreatives={fields.length}
                          pageOptions={pageOptions}
                          instagramOptions={instagramOptions}
                          createUploadUrlForSlot={createUploadUrlForKYD}
                          uploadRefs={uploadRefs}
                          onUpdate={updateCreative}
                          onRemove={removeCreative}
                          onAddMore={addCreative}
                          onFacebookPageChange={onRefetchInstagramForPage}
                          editingName={editingCreativeName}
                          onEditingNameChange={setEditingCreativeName}
                          selectContainerStyles={selectContainerStyles}
                        />
                      ))}
                    </VStack>
                  )}
                </Box>

                {/* Audience */}
                <Box py={4} borderBottomWidth="1px" borderColor="gray.100">
                  <SectionHeader
                    title="Audience Targeting"
                    isComplete={isAudienceSectionComplete}
                    isCollapsed={!audienceOpen}
                    onToggle={() => setAudienceOpen((o) => !o)}
                  />
                  {audienceOpen && (
                    <VStack align="stretch" spacing={4}>
                      <Box
                        py={4}
                        px={6}
                        bgColor="white"
                        rounded={"lg"}
                        border={"1px solid"}
                        borderColor="gray.200"
                      >
                        <HStack justify="space-between" w="full" mb={2}>
                          <VStack align="flex-start">
                            <HStack>
                              <Text fontSize="md" fontWeight={"semibold"}>
                                Smart Targeting
                              </Text>
                              <Badge
                                color="green.100"
                                backgroundColor="green.500"
                                size="md"
                                rounded={"md"}
                                textTransform={"none"}
                                fontWeight={"semibold"}
                                px={"8px"}
                                py={"2px"}
                              >
                                Recommended
                              </Badge>
                            </HStack>
                            <Text fontSize="sm" color="gray.600">
                              We use existing KYD data and Meta AI to find the
                              best target audiences for this campaign.
                            </Text>
                          </VStack>
                          <Switch
                            size="lg"
                            isChecked={!!smartTargeting}
                            onChange={(e) => {
                              setValue("smart_targeting", e.target.checked, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }}
                          />
                        </HStack>
                      </Box>
                      {!smartTargeting && (
                        <AudienceCard
                          ad_account_id={draft.adAccountId}
                          pages={pages.map((p) => ({
                            id: p.id,
                            name: p.name,
                          }))}
                          instagramAccounts={instagramAccounts.map((i) => ({
                            id: i.id,
                            username: i.username,
                          }))}
                          audiences={customAudiences}
                          isEmbedded={true}
                        />
                      )}
                    </VStack>
                  )}
                </Box>

                {/* Budget & Schedule */}
                <Box py={4} borderBottomWidth="1px" borderColor="gray.100">
                  <SectionHeader
                    title="Budget & Schedule"
                    isComplete={isBudgetSectionComplete}
                    isCollapsed={!budgetOpen}
                    onToggle={() => setBudgetOpen((o) => !o)}
                  />
                  {budgetOpen && (
                    <Stack spacing={4} w="full" pt={4}>
                      {/* Campaign budget card */}
                      <Box
                        p={4}
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="gray.200"
                        bg="white"
                        w="full"
                      >
                        <Text
                          fontSize="md"
                          fontWeight="semibold"
                          color="gray.800"
                          mb={1}
                        >
                          Campaign budget
                        </Text>
                        <Text fontSize="sm" color="gray.600" mb={4}>
                          Meta will automatically distribute your budget to the
                          best opportunities across your campaign.
                        </Text>
                        <HStack spacing={4} align="stretch" w="full">
                          <Box flex={1} w="50%">
                            <ChakraSelect
                              value={
                                budget?.type === "daily" ? "daily" : "lifetime"
                              }
                              onChange={(e) => {
                                setBudgetUpdatedByUser(true);
                                const nextType = e.target.value as
                                  | "daily"
                                  | "lifetime";
                                setValue("budget.type", nextType, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }}
                              size="md"
                              borderRadius="md"
                              borderColor="gray.200"
                              bg="white"
                            >
                              <option value="lifetime">Lifetime budget</option>
                              <option value="daily">Daily budget</option>
                            </ChakraSelect>
                          </Box>
                          <InputGroup flex={1} w="50%">
                            <InputLeftAddon
                              borderColor="gray.200"
                              bg="white"
                              color="gray.600"
                              borderRightWidth="0"
                              children="$"
                            />
                            <Input
                              type="number"
                              step={1}
                              min={0}
                              placeholder="0.00"
                              value={
                                budget?.amount?.amount != null
                                  ? String(budget.amount.amount)
                                  : ""
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                const num = v === "" ? null : parseFloat(v);
                                setBudgetUpdatedByUser(true);
                                setValue("budget.amount.amount", num, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }}
                              borderColor="gray.200"
                              bg="white"
                              borderRadius="md"
                              pl={3}
                            />
                          </InputGroup>
                        </HStack>
                      </Box>

                      {/* Schedule (start to end) card */}
                      <Box
                        p={4}
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="gray.200"
                        bg="white"
                        w="full"
                      >
                        <Text fontWeight="semibold" color="gray.800" mb={1}>
                          Schedule (start to end)
                        </Text>
                        <Text fontSize="sm" color="gray.600" mb={4}>
                          How long this ad should run
                        </Text>
                        <Stack
                          spacing={2}
                          align="stretch"
                          w="full"
                          direction={{ base: "column", "2xl": "row" }}
                        >
                          <Box flex={1} position="relative">
                            <WhiteBgInput
                              type="datetime-local"
                              value={scheduleStart}
                              onChange={(e) =>
                                setValue("schedule.start_at", e.target.value, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                })
                              }
                              py={3}
                              w="full"
                              fontSize="sm"
                            />
                          </Box>
                          <Text
                            flexShrink={0}
                            color="gray.500"
                            fontWeight="medium"
                            px={1}
                            alignSelf="center"
                          >
                            →
                          </Text>
                          <Box flex={1} position="relative">
                            <WhiteBgInput
                              type="datetime-local"
                              value={scheduleEnd}
                              onChange={(e) =>
                                setValue("schedule.end_at", e.target.value, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                })
                              }
                              py={3}
                              w="full"
                              fontSize="sm"
                            />
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                </Box>

                {/* Review Campaign button */}
                <HStack mt={4} justify="space-between" w="full">
                  <Button
                    variant="ghost"
                    size="md"
                    bgColor="white"
                    onClick={() => setIsExitConfirmOpen(true)}
                  >
                    <FaChevronLeft style={{ marginRight: "8px" }} />
                    Back
                  </Button>
                  <Button
                    bgColor="gray.900"
                    color="white"
                    _disabled={{
                      bgColor: "gray.300",
                      color: "white",
                      _hover: { bgColor: "gray.300" },
                      cursor: "not-allowed",
                    }}
                    rightIcon={<ChevronRightIcon />}
                    onClick={handleReviewCampaign}
                    isDisabled={!isValid || !hasRequiredAudience || launching}
                    isLoading={launching}
                    size="md"
                  >
                    Review Campaign
                  </Button>
                </HStack>
              </VStack>
            </Container>

            {/* Right panel – preview */}
            <Box
              flex={"1"}
              rounded="xl"
              alignSelf="stretch"
              display={{
                base: mobilePanel === "preview" ? "block" : "none",
                md: "block",
              }}
            >
              <Tabs variant="soft-rounded" colorScheme="gray" size="sm">
                <TabList bg="gray.200" rounded="lg" p={1} maxW="80%" mx="auto">
                  <Tab
                    _selected={{ bg: "white", shadow: "sm" }}
                    rounded="md"
                    width={"50%"}
                  >
                    <Text fontSize="xs">Instagram Preview</Text>
                  </Tab>
                  <Tab
                    _selected={{ bg: "white", shadow: "sm" }}
                    rounded="md"
                    width={"50%"}
                  >
                    <Text fontSize="xs">Facebook Preview</Text>
                  </Tab>
                </TabList>
                <TabPanels pt={4}>
                  <TabPanel px={0}>
                    <VStack align="stretch" spacing={6} w="full">
                      {fields.map((field, index) => {
                        const creative = creatives[index];
                        if (!creative) return null;
                        return (
                          <Stack
                            key={`instagram-preview-${field.id}`}
                            spacing={6}
                          >
                            <InstagramPostPreview
                              username={
                                creative.instagramAccount?.username ?? "Account"
                              }
                              message={creative.primaryText}
                              ctaText={ctaText}
                              mediaUrls={
                                creative.squareMediaUrl
                                  ? [creative.squareMediaUrl]
                                  : []
                              }
                              profilePictureUrl={
                                creative.instagramAccount
                                  ?.profile_picture_url || undefined
                              }
                              mediaType="single"
                              fullWidth={true}
                            />
                            <InstagramStoryPreview
                              username={
                                creative.instagramAccount?.username ?? "Account"
                              }
                              ctaText={ctaText}
                              mediaUrls={
                                creative.verticalMediaUrl
                                  ? [creative.verticalMediaUrl]
                                  : []
                              }
                              profilePictureUrl={
                                creative.instagramAccount
                                  ?.profile_picture_url || undefined
                              }
                              mediaType="single"
                              fullWidth={true}
                            />
                          </Stack>
                        );
                      })}
                    </VStack>
                  </TabPanel>
                  <TabPanel px={0}>
                    <VStack align="stretch" spacing={6} w="full">
                      {fields.map((field, index) => {
                        const creative = creatives[index];
                        if (!creative) return null;
                        return (
                          <Stack
                            key={`facebook-preview-${field.id}`}
                            spacing={6}
                          >
                            <FacebookFeedPreview
                              message={creative.primaryText}
                              ctaText={ctaText}
                              mediaUrls={
                                creative.squareMediaUrl
                                  ? [creative.squareMediaUrl]
                                  : []
                              }
                              mediaType="single"
                              pageName={creative.fbPage?.name ?? "Account"}
                              profilePictureUrl={
                                creative.fbPage?.picture || undefined
                              }
                              websiteUrl={websiteUrl}
                              eventName={eventName}
                              description={eventDescription}
                              fullWidth={true}
                            />
                            <FacebookStoryPreview
                              message={creative.primaryText}
                              ctaText={ctaText}
                              mediaUrls={
                                creative.verticalMediaUrl
                                  ? [creative.verticalMediaUrl]
                                  : []
                              }
                              mediaType="single"
                              pageName={creative.fbPage?.name ?? "Account"}
                              profilePictureUrl={
                                creative.fbPage?.picture || undefined
                              }
                              websiteUrl={websiteUrl}
                              eventName={eventName}
                              description={eventDescription}
                              fullWidth={true}
                            />
                          </Stack>
                        );
                      })}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </HStack>
        </VStack>
      </FormProvider>
      {loadingModal}
      {publishStatusToast}
      {leaveEditorModal}
    </>
  );
}
